/**
 * Image compression utility to prevent browser crashes when uploading large images
 */

interface CompressionOptions {
  maxWidth?: number;
  maxHeight?: number;
  quality?: number;
  maxSizeMB?: number;
}

const DEFAULT_OPTIONS: CompressionOptions = {
  maxWidth: 1920,
  maxHeight: 1080,
  quality: 0.8,
  maxSizeMB: 2, // Target max 2MB
};

/**
 * Compresses an image file to reduce size and prevent browser memory issues
 * @param file The original image file
 * @param options Compression options
 * @returns A promise that resolves to the compressed file
 */
export async function compressImage(
  file: File,
  options: CompressionOptions = {}
): Promise<File> {
  const { maxWidth, maxHeight, quality, maxSizeMB } = { ...DEFAULT_OPTIONS, ...options };

  // If file is small enough (< 500KB), return original
  if (file.size < 500 * 1024) {
    return file;
  }

  return new Promise((resolve, reject) => {
    const reader = new FileReader();

    reader.onload = (event) => {
      const img = new Image();

      img.onload = () => {
        try {
          // Calculate new dimensions
          let { width, height } = img;

          if (width > maxWidth! || height > maxHeight!) {
            const aspectRatio = width / height;

            if (width > height) {
              width = Math.min(width, maxWidth!);
              height = width / aspectRatio;
            } else {
              height = Math.min(height, maxHeight!);
              width = height * aspectRatio;
            }
          }

          // Create canvas and draw image
          const canvas = document.createElement('canvas');
          canvas.width = width;
          canvas.height = height;

          const ctx = canvas.getContext('2d');
          if (!ctx) {
            reject(new Error('Failed to get canvas context'));
            return;
          }

          ctx.drawImage(img, 0, 0, width, height);

          // Convert to blob with compression
          canvas.toBlob(
            (blob) => {
              if (!blob) {
                reject(new Error('Failed to compress image'));
                return;
              }

              // If still too large and quality can be reduced, try again
              const targetSize = (maxSizeMB || 2) * 1024 * 1024;
              if (blob.size > targetSize && quality! > 0.5) {
                // Recursively compress with lower quality
                const newFile = new File([blob], file.name, { type: 'image/jpeg' });
                compressImage(newFile, { ...options, quality: quality! - 0.1 })
                  .then(resolve)
                  .catch(reject);
                return;
              }

              // Create new file with compressed blob
              const compressedFile = new File(
                [blob],
                file.name.replace(/\.[^.]+$/, '.jpg'),
                { type: 'image/jpeg' }
              );

              console.log(`Image compressed: ${(file.size / 1024 / 1024).toFixed(2)}MB -> ${(compressedFile.size / 1024 / 1024).toFixed(2)}MB`);
              resolve(compressedFile);
            },
            'image/jpeg',
            quality
          );
        } catch (err) {
          reject(err);
        }
      };

      img.onerror = () => {
        reject(new Error('Failed to load image'));
      };

      img.src = event.target?.result as string;
    };

    reader.onerror = () => {
      reject(new Error('Failed to read file'));
    };

    reader.readAsDataURL(file);
  });
}

/**
 * Validates that a file is an acceptable image
 */
export function validateImageFile(file: File): { valid: boolean; error?: string } {
  const validTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/webp'];

  if (!validTypes.includes(file.type)) {
    return {
      valid: false,
      error: 'Please select a valid image file (JPG, PNG, GIF, or WebP)',
    };
  }

  // 2GB hard limit (server limit)
  if (file.size > 2 * 1024 * 1024 * 1024) {
    return {
      valid: false,
      error: 'Image is too large. Maximum size is 2GB.',
    };
  }

  return { valid: true };
}
